import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import TcoatProductRequestsTable from '../commercialRequests/TcoatProductRequestsTable';
import TcoatProductRequestItemsTable from '../commercialRequests/TcoatProductRequestItemsTable';
import RequestListViewTabs from '../commercialRequests/RequestListViewTabs';
import { TcoatProductRequestForm, emptyTcoatProductRequestForm } from '../forms/TcoatProductRequestForm';
import TcoatProductRequestDetailModal from './TcoatProductRequestDetailModal';
import productionProductRequestsAPI from '../../services/productionProductRequestsAPI';
import { useUIColors } from '../../hooks/useTheme';
import { useAuth } from '../../contexts/AuthContext';
import API_BASE_URL from '../../config/api';
import { authFetch } from '../../services/apiClient';
import {
  notifyProductionProductRequestsChanged,
  PRODUCTION_PRODUCT_REQUESTS_CHANGED_EVENT,
} from './commercialRequestsUtils';
import {
  buildDefaultLineSelections,
  isUpsellingQuotationItem,
  partitionQuotationLineItems,
  selectedQuotationLines,
  updateLineSelectionMap,
  validateLineSelections,
} from './quotationItemClassification';
import { REQUEST_STATUS } from './planningDomain';

const saleDetailLineItems = (detail) => {
  if (Array.isArray(detail?.items)) return detail.items;
  if (Array.isArray(detail?.quotation_items)) return detail.quotation_items;
  return [];
};

const tcoatLinesFromSaleDetail = (detail, paintSkuSet, upsellingSkuSet) => {
  const lineItems = saleDetailLineItems(detail);
  const { production } = partitionQuotationLineItems(lineItems, paintSkuSet, upsellingSkuSet);
  if (production.length) return production;
  // Si el catálogo T-COAT aún no cargó o el SKU no matchea, no ocultar la venta entera.
  const withoutUpselling = lineItems.filter(
    (item) => !isUpsellingQuotationItem(item, paintSkuSet, upsellingSkuSet)
  );
  return withoutUpselling.length ? withoutUpselling : lineItems;
};

const TcoatProductRequestsPanel = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [itemRows, setItemRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [error, setError] = useState('');
  const [itemsError, setItemsError] = useState('');
  const [listTab, setListTab] = useState('requests');
  const [detailRequest, setDetailRequest] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyTcoatProductRequestForm);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [sales, setSales] = useState([]);
  const [paintSkuSet, setPaintSkuSet] = useState(() => new Set());
  const [upsellingSkuSet, setUpsellingSkuSet] = useState(() => new Set());
  const [saleLineItems, setSaleLineItems] = useState([]);
  const [selectionMap, setSelectionMap] = useState({});
  const [saleLinesLoading, setSaleLinesLoading] = useState(false);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await productionProductRequestsAPI.getAll();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'No se pudieron cargar las solicitudes T-COAT.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadItems = useCallback(async () => {
    setItemsLoading(true);
    setItemsError('');
    try {
      const data = await productionProductRequestsAPI.getAllItems();
      setItemRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setItemsError(err?.message || 'No se pudieron cargar los ítems T-COAT.');
      setItemRows([]);
    } finally {
      setItemsLoading(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadRequests(), loadItems()]);
  }, [loadRequests, loadItems]);

  const loadLookups = useCallback(async () => {
    try {
      const [salesRes, paintRes, upsellingRes] = await Promise.all([
        authFetch(`${API_BASE_URL}/api/quotations/sales`, { cache: 'no-store' }),
        authFetch(`${API_BASE_URL}/api/quotations/products`, { cache: 'no-store' }).catch(() => null),
        authFetch(`${API_BASE_URL}/api/quotations/upselling-products`, { cache: 'no-store' }).catch(
          () => null
        ),
      ]);
      if (salesRes?.ok) {
        const raw = await salesRes.json();
        setSales(Array.isArray(raw) ? raw : []);
      } else {
        setSales([]);
      }
      if (paintRes?.ok) {
        const raw = await paintRes.json();
        const set = new Set();
        (Array.isArray(raw) ? raw : []).forEach((row) => {
          const key = String(row.sku || '').trim().toUpperCase();
          if (key) set.add(key);
        });
        setPaintSkuSet(set);
      }
      if (upsellingRes?.ok) {
        const raw = await upsellingRes.json();
        const set = new Set();
        (Array.isArray(raw) ? raw : []).forEach((row) => {
          [row.upselling_code, row.legacy_code, row.legacy_code2].forEach((code) => {
            const key = String(code || '').trim().toUpperCase();
            if (key) set.add(key);
          });
        });
        setUpsellingSkuSet(set);
      }
    } catch (err) {
      console.error('Error loading T-COAT request lookups:', err);
    }
  }, []);

  useEffect(() => {
    refreshAll();
    loadLookups();
  }, [refreshAll, loadLookups]);

  useEffect(() => {
    const refresh = () => refreshAll();
    window.addEventListener(PRODUCTION_PRODUCT_REQUESTS_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(PRODUCTION_PRODUCT_REQUESTS_CHANGED_EVENT, refresh);
  }, [refreshAll]);

  const saleOptions = useMemo(() => {
    const seen = new Set();
    return (sales || [])
      .map((sale) => {
        const id = Number(sale?.id || sale?.sale_id || 0);
        if (!id || seen.has(id)) return null;
        seen.add(id);
        const code = String(sale?.sale_code || `V-${id}`).trim();
        const client = String(sale?.organization_name || 'Sin cliente').trim();
        const quote = String(sale?.quotation_code || '').trim();
        return {
          value: String(id),
          label: `${code}${quote ? ` · ${quote}` : ''} · ${client}`,
          sale_code: code,
          quotation_code: quote,
          sort_key: id,
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        const byCode = String(b.sale_code || '').localeCompare(String(a.sale_code || ''), 'es', {
          numeric: true,
          sensitivity: 'base',
        });
        return byCode !== 0 ? byCode : Number(b.sort_key || 0) - Number(a.sort_key || 0);
      });
  }, [sales]);

  const listTabs = useMemo(
    () => [
      { key: 'requests', label: 'Solicitudes', count: rows.length },
      { key: 'items', label: 'Ítems', count: itemRows.length },
    ],
    [rows.length, itemRows.length]
  );

  const listSubtitle = loading
    ? 'Cargando…'
    : listTab === 'items'
      ? itemsError || `${itemRows.length} ítems · producción de pintura`
      : error || `${rows.length} solicitud${rows.length === 1 ? '' : 'es'} · producción de pintura`;

  const resetSaleLines = () => {
    setSaleLineItems([]);
    setSelectionMap({});
    setSaleLinesLoading(false);
  };

  const openCreate = () => {
    setIsEditing(false);
    setEditingId(null);
    setFormError('');
    setFormData({
      ...emptyTcoatProductRequestForm(),
      reference: String(user?.full_name || user?.name || '').trim(),
    });
    resetSaleLines();
    setShowForm(true);
  };

  const handleSaleChange = async (saleIdValue) => {
    const saleId = Number(saleIdValue || 0);
    if (!saleId) {
      setFormData((prev) => ({
        ...prev,
        sale_id: '',
        sale_code: '',
        quotation_code: '',
        reference: prev.reference,
      }));
      resetSaleLines();
      return;
    }
    const option = saleOptions.find((row) => String(row.value) === String(saleId));
    setFormData((prev) => ({
      ...prev,
      sale_id: String(saleId),
      sale_code: option?.sale_code || '',
      quotation_code: option?.quotation_code || '',
    }));
    setSaleLinesLoading(true);
    try {
      const response = await authFetch(`${API_BASE_URL}/api/quotations/sales/${saleId}/detail`, {
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const detail = await response.json();
      const saleCode = String(detail?.sale?.sale_code || option?.sale_code || '').trim();
      const quotationCode = String(
        detail?.quotation?.quotation_code ||
          detail?.sale?.quotation_code ||
          option?.quotation_code ||
          ''
      ).trim();
      const production = tcoatLinesFromSaleDetail(detail, paintSkuSet, upsellingSkuSet);
      setFormData((prev) => ({
        ...prev,
        sale_id: String(saleId),
        sale_code: saleCode,
        quotation_code: quotationCode,
      }));
      setSaleLineItems(production);
      setSelectionMap(buildDefaultLineSelections(production));
    } catch (err) {
      console.error(err);
      setSaleLineItems([]);
      setSelectionMap({});
      setFormError(err?.message || 'No se pudieron cargar los ítems de la venta.');
    } finally {
      setSaleLinesLoading(false);
    }
  };

  const handleSelectionUpdate = (itemId, field, value) => {
    setSelectionMap((prev) => updateLineSelectionMap(prev, itemId, field, value));
  };

  const openEdit = async (request) => {
    const source = request?.items ? request : await productionProductRequestsAPI.getById(request.id);
    setDetailRequest(null);
    setIsEditing(true);
    setEditingId(source.id);
    setFormError('');
    resetSaleLines();
    setFormData({
      request_code: source.request_code || '',
      origin: source.origin || 'Comercial',
      reference: source.reference || '',
      needed_by_date: source.needed_by_date ? String(source.needed_by_date).slice(0, 10) : '',
      needed_by_time: source.needed_by_time ? String(source.needed_by_time).slice(0, 5) : '',
      status: source.status || REQUEST_STATUS.requested,
      quotation_code: source.quotation_code || '',
      sale_id: '',
      sale_code: source.sale_code || '',
      notes: source.notes || '',
      items:
        Array.isArray(source.items) && source.items.length
          ? source.items.map((item) => ({
              product_code: item.product_code || '',
              product_name: item.product_name || '',
              color_code: item.color_code || '',
              color_name: item.color_name || '',
              formula_kind: item.formula_kind || 'color',
              quantity: item.quantity ?? 1,
              unit: item.unit || 'L',
            }))
          : emptyTcoatProductRequestForm().items,
    });
    setShowForm(true);
  };

  const openDetail = async (row) => {
    if (!row?.id) return;
    try {
      const full = await productionProductRequestsAPI.getById(row.id);
      setDetailRequest(full);
    } catch (err) {
      console.error(err);
      setDetailRequest(row);
    }
  };

  const handleChange = (key, value) => {
    if (key === 'needed_by_deadline') {
      setFormData((prev) => ({
        ...prev,
        needed_by_date: value?.date || '',
        needed_by_time: value?.time || '',
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    const reference = String(formData.reference || '').trim();
    if (!reference) {
      setFormError('El solicitante es obligatorio.');
      return;
    }

    let items = [];
    let saleCode = String(formData.sale_code || '').trim();
    let quotationCode = String(formData.quotation_code || '').trim();

    if (!isEditing) {
      if (!String(formData.sale_id || '').trim()) {
        setFormError('Debes anexar un código de venta.');
        return;
      }
      const selectionError = validateLineSelections(saleLineItems, selectionMap, 'producción T-COAT');
      if (selectionError) {
        setFormError(selectionError);
        return;
      }
      items = selectedQuotationLines(saleLineItems, selectionMap).map((item) => ({
        product_code: String(item.product_sku || item.item_code || '').trim(),
        product_name: String(item.item_name || item.product_name || '').trim(),
        color_code: String(item.color_code || '').trim(),
        color_name: String(item.color || item.color_name || '').trim(),
        formula_kind: 'color',
        quantity: Number(item.requested_quantity || 0),
        unit: String(item.unit || 'L').trim() || 'L',
      }));
    } else {
      items = (formData.items || [])
        .map((item) => ({
          product_code: String(item.product_code || '').trim(),
          product_name: String(item.product_name || '').trim(),
          color_code: String(item.color_code || '').trim(),
          color_name: String(item.color_name || '').trim(),
          formula_kind: String(item.formula_kind || 'color').trim() || 'color',
          quantity: Number(item.quantity || 0),
          unit: String(item.unit || 'L').trim() || 'L',
        }))
        .filter((item) => item.product_code || item.product_name);
      if (!items.length) {
        setFormError('Agrega al menos un producto con código o nombre.');
        return;
      }
    }

    const neededByTime = String(formData.needed_by_time || '').trim().slice(0, 5);
    const payload = {
      origin: String(formData.origin || 'Comercial').trim() || 'Comercial',
      reference,
      needed_by_date: formData.needed_by_date || null,
      status: String(formData.status || '').trim() || REQUEST_STATUS.requested,
      quotation_code: quotationCode,
      sale_code: saleCode,
      source: isEditing ? '' : 'commercial_manual',
      notes: String(formData.notes || '').trim(),
      items,
      created_by: reference,
    };

    setIsSaving(true);
    setFormError('');
    try {
      if (isEditing && editingId) {
        await productionProductRequestsAPI.update(editingId, payload);
        if (neededByTime) {
          await productionProductRequestsAPI.updatePlanning(editingId, {
            needed_by_date: formData.needed_by_date || null,
            needed_by_time: neededByTime,
          });
        }
      } else {
        const created = await productionProductRequestsAPI.create(payload);
        const createdId = Number(created?.id || 0);
        if (createdId && neededByTime) {
          await productionProductRequestsAPI.updatePlanning(createdId, {
            needed_by_date: formData.needed_by_date || null,
            needed_by_time: neededByTime,
          });
        }
      }
      setShowForm(false);
      setEditingId(null);
      setFormData(emptyTcoatProductRequestForm());
      resetSaleLines();
      await refreshAll();
      notifyProductionProductRequestsChanged();
    } catch (err) {
      setFormError(err?.message || 'No se pudo guardar la solicitud.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBulkDelete = async (ids) => {
    if (!ids?.length) return false;
    const ok = window.confirm(
      ids.length === 1 ? '¿Eliminar esta solicitud T-COAT?' : `¿Eliminar ${ids.length} solicitudes T-COAT?`
    );
    if (!ok) return false;
    await productionProductRequestsAPI.bulkDelete(ids);
    await refreshAll();
    notifyProductionProductRequestsChanged();
    return true;
  };

  return (
    <>
      <div
        style={{
          border: `1px solid ${ui.border}`,
          borderRadius: 12,
          overflow: 'hidden',
          background: ui.page,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>
              Solicitudes de productos T-COAT
            </h3>
            <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>{listSubtitle}</p>
          </div>
          <button
            type="button"
            onClick={openCreate}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 36,
              padding: '0 14px',
              borderRadius: 8,
              border: 'none',
              background: '#16a34a',
              color: '#fff',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            <Plus size={16} /> Nueva solicitud
          </button>
        </div>

        <RequestListViewTabs
          tabs={listTabs}
          activeKey={listTab}
          onChange={setListTab}
          isDarkMode={isDarkMode}
          ariaLabel="Vistas de solicitudes T-COAT"
        />

        {listTab === 'items' ? (
          <TcoatProductRequestItemsTable
            rows={itemRows}
            loading={itemsLoading}
            error={itemsError}
            isDarkMode={isDarkMode}
            hideChrome
            onRequestClick={openDetail}
          />
        ) : (
          <TcoatProductRequestsTable
            rows={rows}
            loading={loading}
            error={error}
            isDarkMode={isDarkMode}
            hideChrome
            attached
            onRowClick={openDetail}
            onBulkDelete={handleBulkDelete}
          />
        )}
      </div>

      <TcoatProductRequestForm
        isOpen={showForm}
        onClose={() => {
          if (isSaving) return;
          setShowForm(false);
          resetSaleLines();
        }}
        onSave={handleSave}
        isSaving={isSaving}
        isDarkMode={isDarkMode}
        isEditing={isEditing}
        formData={formData}
        onChange={handleChange}
        formError={formError}
        saleOptions={saleOptions}
        saleLineItems={saleLineItems}
        selectionMap={selectionMap}
        onSelectionUpdate={handleSelectionUpdate}
        saleLinesLoading={saleLinesLoading}
        onSaleChange={handleSaleChange}
      />

      <TcoatProductRequestDetailModal
        isOpen={Boolean(detailRequest)}
        onClose={() => setDetailRequest(null)}
        request={detailRequest}
        isDarkMode={isDarkMode}
        onEdit={openEdit}
      />
    </>
  );
};

export default TcoatProductRequestsPanel;
